import PropTypes from 'prop-types'
import { PropTypes as MobXPropTypes } from 'mobx-react'
import React, { Component } from 'react'

import {
  getConfig,
  makeDataUrl,
  formatDate,
  formatMonth,
  listGetById
} from '../../shared/utils'

import JumpToStep from '../../shared/components/JumpToStep'
import FormSection from '../../shared/components/FormSection'

const hasValue = (image, fieldName, defaultValue = undefined) => {
  if (image[fieldName]) {
    return image[fieldName]
  }

  return image.pivot && image.pivot[fieldName]
    ? image.pivot[fieldName]
    : defaultValue
}

export default class OverviewSection extends Component {
  static propTypes = {
    bookableOutOfOpeningTimes: PropTypes.bool,
    bookableOutOfSeason: PropTypes.bool,
    bookingEnd: PropTypes.string,
    bookingStart: PropTypes.string,
    costsByPerson: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    curriculums: PropTypes.string,
    days: MobXPropTypes.observableArray,
    description: PropTypes.string,
    duration: PropTypes.number,
    expireAt: PropTypes.string,
    freeEntryForTeachersAtPrevisit: PropTypes.bool,
    generalPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    goToSection: PropTypes.func,
    grades: MobXPropTypes.observableArray,
    images: MobXPropTypes.observableArray,
    isFieldGroupDirty: PropTypes.func,
    languages: MobXPropTypes.observableArray,
    links: MobXPropTypes.observableArray,
    materialCosts: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    materials: MobXPropTypes.observableArray,
    maxParticipants: PropTypes.number,
    meetingPoint: PropTypes.string,
    methods: MobXPropTypes.observableArray,
    minPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    schools: MobXPropTypes.observableArray,
    show: PropTypes.bool,
    specialConditionsForRemedialClasses: PropTypes.bool,
    steps: PropTypes.array,
    subjects: MobXPropTypes.observableArray,
    title: PropTypes.string
  };

  renderPlainList(data, listName, separator = <br />) {
    return (data || [])
      .map((value) => {
        return [
          listGetById(
            listName,
            typeof value === 'object' && 'id' in value ? value.id : value
          ),
          ...('amount' in value && value.amount !== undefined
            ? [`: ${value.amount}`]
            : []),
          separator
        ]
      })
      .reduce((memo, entry) => [...memo, ...entry], [])
  }

  renderList(data, listName, className) {
    return data && data.length ? (
      <ul className={className}>
        {(data || [])
          .map((entry) => {
            return entry
          })
          .map(entry => ('pivot' in entry ? entry.pivot : entry))
          .map((entry) => {
            return (
              <li key={`list-entry-${entry.id}`}>
                {getConfig(listName)
                  // eslint-disable-next-line eqeqeq
                  .filter(value => entry.id == value.id)
                  .map((value) => {
                    if ('amount' in entry) {
                      return (
                        <span key={`value-entry-${value.id}`}>
                          <span className="info-list-label">
                            {value.title}
                            :&nbsp;
                          </span>
                          {entry.amount}
                        </span>
                      )
                    }

                    return value.title
                  })}
              </li>
            )
          })}
      </ul>
    ) : (
      <em>Keine Daten vorhanden</em>
    )
  }

  render() {
    const {
      title,
      duration,
      maxParticipants,
      meetingPoint,
      description,
      expireAt,
      bookingStart,
      bookingEnd,
      bookableOutOfOpeningTimes,
      generalPrice,
      languages,
      // materialCosts,
      minPrice,
      costsByPerson,
      schools,
      grades,
      subjects,
      days,
      materials,
      methods,
      links,
      images,
      curriculums,
      specialConditionsForRemedialClasses,
      freeEntryForTeachersAtPrevisit,
      show,
      steps,
      goToSection
    } = this.props

    return (
      <FormSection title="6. Übersicht" className="overview" show={show}>
        <div className="data-group">
          <JumpToStep onClick={goToSection} steps={steps} which="general">
            <h3>1. Allgemeine Informationen</h3>
            <strong>{title}</strong>
            <dl>
              <dt>Dauer:</dt>
              <dd>
                {duration}
                {' '}
                (Minuten)
              </dd>

              <dt>Teilnehmer:</dt>
              <dd>
                {maxParticipants}
                {' '}
                (maximal)
              </dd>

              <dt>Treffpunkt:</dt>
              <dd>{meetingPoint}</dd>

              <dt>Beschreibung:</dt>
              <dd>{description}</dd>

              <dt>Läuft aus am:</dt>
              <dd>{formatDate(expireAt) || 'Keine Angabe'}</dd>
            </dl>
          </JumpToStep>
        </div>

        <div className="data-group">
          <JumpToStep onClick={goToSection} steps={steps} which="reference">
            <h3>2. Bezug zur Schule</h3>
            <dl>
              <dt>Schulen:</dt>
              <dd>{this.renderPlainList(schools, 'list_school')}</dd>

              <dt>Lehrplan:</dt>
              <dd>{curriculums}</dd>

              <dt>Jahrgangsstufe:</dt>
              <dd>
                {(grades || [])
                  .filter(value => value && !!value.id)
                  .map(value => [
                    value.id,
                    <span key={`separator-${value.id}`} className="separator">
                      ,
                    </span>
                  ])
                  .reduce((memo, entry) => [...memo, ...entry], [])}
              </dd>

              <dt>Schulfächer:</dt>
              <dd>{this.renderPlainList(subjects, 'list_subject')}</dd>

              <dt>Angebotene Sprachen:</dt>
              <dd>{this.renderPlainList(languages, 'list_languages')}</dd>
            </dl>
          </JumpToStep>
        </div>

        <div className="data-group">
          <JumpToStep onClick={goToSection} steps={steps} which="booking">
            <h3>3. Buchung &amp; Kosten</h3>
            <dl>
              <dt>buchbar von</dt>
              <dd>
                {formatMonth(bookingStart)}
                {' '}
                bis
                {formatMonth(bookingEnd)}
                <br />
                {bookableOutOfOpeningTimes
                  ? 'auch außerhalb der Museumsöffnungszeiten buchbar'
                  : 'nicht außerhalb der Museumsöffnungszeiten'}
              </dd>
              <dt>Wochentage:</dt>
              <dd>
                {this.renderPlainList(
                  days,
                  'list_day',
                  <span className="separator">, </span>
                )}
              </dd>
            </dl>

            <div>
              <strong>Kosten</strong>
            </div>
            <dl>
              {(typeof generalPrice !== 'undefined' && parseFloat(generalPrice))
                ? [
                  <dt key="key">Pauschalbetrag:</dt>,
                  <dd key="val">
                    {generalPrice}
                    {' '}
                    Euro
                  </dd>
                ]
                : null}
              {/* {typeof materialCosts !== "undefined"
                ? [
                    <dt key="key">Materialkosten pro Schüler:</dt>,
                    <dd key="val">{materialCosts} Euro</dd>
                  ]
                : null} */}
              {(typeof minPrice !== 'undefined' && parseFloat(minPrice))
                ? [
                  <dt key="key">Mindestbetrag:</dt>,
                  <dd key="val">
                    {minPrice}
                    {' '}
                    Euro
                  </dd>
                ]
                : null}
              {(typeof costsByPerson !== 'undefined' && parseFloat(costsByPerson))
                ? [
                  <dt key="key">Kosten pro Schüler:</dt>,
                  <dd key="val">
                    {costsByPerson}
                    {' '}
                    Euro
                  </dd>
                ]
                : null}
              {typeof specialConditionsForRemedialClasses !== 'undefined'
                ? [
                  <dt key="key">
                    Gesonderte Konditionen für Förderklassen:
                  </dt>,
                  <dd key="val">
                    {specialConditionsForRemedialClasses ? 'ja' : 'nein'}
                  </dd>
                ]
                : null}
              {typeof freeEntryForTeachersAtPrevisit !== 'undefined'
                ? [
                  <dt key="key">
                    Freier Eintritt für Lehrkräfte bei Vorbesuchen:
                  </dt>,
                  <dd key="val">
                    {freeEntryForTeachersAtPrevisit ? 'ja' : 'nein'}
                  </dd>
                ]
                : null}
            </dl>
          </JumpToStep>
        </div>

        <div className="data-group">
          <JumpToStep onClick={goToSection} steps={steps} which="material">
            <h3>4. Materialien</h3>

            <dl>
              <dt>benutzte Materialien:</dt>
              <dd>{this.renderPlainList(materials, 'list_material')}</dd>
              <dt>Methoden:</dt>
              <dd>{this.renderPlainList(methods, 'list_method')}</dd>
            </dl>
          </JumpToStep>
        </div>

        <div className="data-group">
          <JumpToStep onClick={goToSection} steps={steps} which="media">
            <h3>5. Medien</h3>

            <ul className="file-list files">
              {(links || []).map(link => (
                <li key={`link-${link.url || (link.file && link.file.name)}`}>
                  <div>
                    <a href={`${link.url}`}>
                      {link.description
                        || (link.pivot && link.pivot.description
                          ? link.pivot.description
                          : '')}
                    </a>
                    &nbsp;
                    <small>
                      (
                      {link.name}
                      )
                    </small>
                  </div>
                </li>
              ))}
            </ul>
            <ul className="file-list images">
              {(images || []).map(image => (
                <li
                  key={`image-${image.url || (image.file && image.file.name)}`}
                >
                  <div
                    className="thumbnail"
                    style={{
                      backgroundImage: `url(${makeDataUrl(
                        image,
                        images !== undefined ? images : null
                      )})`
                    }}
                  />
                  <div>{hasValue(image, 'description', '')}</div>
                  {hasValue(image, 'type', 'image') === 'banner' ? (
                    <span className="label label-default">Banner</span>
                  ) : null}
                </li>
              ))}
            </ul>
          </JumpToStep>
        </div>
      </FormSection>
    )
  }
}



// WEBPACK FOOTER //
// ./src/program/components/06-OverviewSection.js